/**
 * Mobile-only seamless marquee for the Dstrezzed announcement bar.
 * Desktop stays a centered static row with diamond separators.
 */
class AnnouncementMarquee extends HTMLElement {
  /** @type {HTMLElement | null} */
  #bar = null;

  /** @type {HTMLElement | null} */
  #source = null;

  /** @type {Element[]} */
  #originalSlides = [];

  /** @type {MediaQueryList | null} */
  #mobileQuery = null;

  /** @type {MediaQueryList | null} */
  #reduceQuery = null;

  /** @type {MutationObserver | null} */
  #missingSlidesObserver = null;

  connectedCallback() {
    this.#bar = this.closest('.announcement-bar--dstrezzed');
    this.#source = this.querySelector('.announcement-bar__slides');

    if (!this.#bar || !this.#source) return;

    this.#mobileQuery = window.matchMedia('(max-width: 749px)');
    this.#reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#mobileQuery.addEventListener('change', this.#scheduleApply);
    this.#reduceQuery.addEventListener('change', this.#scheduleApply);

    this.#cacheOriginalSlides();

    if (this.#originalSlides.length === 0) {
      this.#waitForSlides();
      return;
    }

    this.#scheduleApply();
  }

  disconnectedCallback() {
    this.#mobileQuery?.removeEventListener('change', this.#scheduleApply);
    this.#reduceQuery?.removeEventListener('change', this.#scheduleApply);
    this.#missingSlidesObserver?.disconnect();
    this.#missingSlidesObserver = null;
  }

  #cacheOriginalSlides() {
    if (!this.#source) return;

    this.#originalSlides = Array.from(this.#source.querySelectorAll(':scope > slideshow-slide'));
  }

  #waitForSlides() {
    this.#missingSlidesObserver?.disconnect();
    this.#missingSlidesObserver = new MutationObserver(() => {
      this.#cacheOriginalSlides();

      if (this.#originalSlides.length === 0) return;

      this.#missingSlidesObserver?.disconnect();
      this.#missingSlidesObserver = null;
      this.#scheduleApply();
    });
    this.#missingSlidesObserver.observe(this, { childList: true, subtree: true });
  }

  #scheduleApply = () => {
    const fontsReady = document.fonts?.ready ?? Promise.resolve();
    const timeout = new Promise((resolve) => setTimeout(resolve, 400));

    Promise.race([fontsReady, timeout]).then(() => {
      requestAnimationFrame(() => this.#apply());
    });
  };

  #apply() {
    if (!this.#bar || !this.#source || this.#originalSlides.length === 0) return;

    if (this.#mobileQuery?.matches) {
      this.#setupMobileMarquee();
      return;
    }

    this.#setupDesktop();
  }

  #setupDesktop() {
    this.#bar?.classList.remove('is-marquee');
    this.#removeLoopClones();
    this.#source?.replaceChildren();
    this.#appendSequence(this.#source, { trailing: false, useOriginals: true });
  }

  #setupMobileMarquee() {
    this.#bar?.classList.remove('is-marquee');
    this.#removeLoopClones();
    this.#source?.replaceChildren();
    this.#appendSequence(this.#source, { trailing: true, useOriginals: true });

    if (!this.#source || !this.#bar) return;

    if (this.#reduceQuery?.matches) return;

    const viewportWidth = this.#bar.clientWidth;
    let copies = 0;

    while (this.#source.scrollWidth < viewportWidth && copies < 8) {
      this.#appendSequence(this.#source, { trailing: true, useOriginals: false });
      copies += 1;
    }

    const loopClone = this.#source.cloneNode(true);

    if (!(loopClone instanceof HTMLElement)) return;

    loopClone.setAttribute('aria-hidden', 'true');

    for (const el of loopClone.querySelectorAll('[ref]')) {
      el.removeAttribute('ref');
    }

    this.appendChild(loopClone);

    const speedMs = parseInt(this.#bar.dataset.speed || '5000', 10) || 5000;
    const pxPerSecond = 250 / (speedMs / 1000);
    const duration = Math.max(8, this.#source.scrollWidth / pxPerSecond);

    this.#bar.style.setProperty('--ann-duration', `${duration}s`);
    this.#bar.style.setProperty('--ann-shift', `${this.#source.scrollWidth}px`);
    this.#bar.classList.add('is-marquee');
  }

  #removeLoopClones() {
    const groups = Array.from(this.querySelectorAll('.announcement-bar__slides'));

    for (const [index, group] of groups.entries()) {
      if (index > 0) group.remove();
    }
  }

  /**
   * @param {HTMLElement | null} root
   * @param {{ trailing: boolean, useOriginals: boolean }} options
   */
  #appendSequence(root, { trailing, useOriginals }) {
    if (!root) return;

    for (const [index, slide] of this.#originalSlides.entries()) {
      if (index > 0) root.appendChild(this.#makeSep());
      root.appendChild(this.#prepareSlide(slide, !useOriginals));
    }

    if (trailing) root.appendChild(this.#makeSep());
  }

  /**
   * @param {Element} slide
   * @param {boolean} isClone
   * @returns {Element}
   */
  #prepareSlide(slide, isClone) {
    const node = isClone ? slide.cloneNode(true) : slide;

    if (!(node instanceof Element)) return slide;

    node.removeAttribute('aria-hidden');

    if (isClone) {
      node.setAttribute('aria-hidden', 'true');
      node.removeAttribute('ref');
    }

    return node;
  }

  #makeSep() {
    const sep = document.createElement('span');
    sep.className = 'ann-dstr__sep';
    sep.setAttribute('aria-hidden', 'true');
    return sep;
  }
}

if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', AnnouncementMarquee);
}
